import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ShieldCheck, Loader2, Plus, Trash2, Save } from "lucide-react";

const SLATE = "#475569";

function ImportanceInput({ value, onChange, testid }) {
  return (
    <div className="flex items-center gap-2">
      <Input type="number" min={1} max={100} value={value}
        onChange={(e) => onChange(Math.max(1, Math.min(100, Number(e.target.value) || 1)))}
        className="h-8 w-20 text-sm" data-testid={testid} />
      <span className="text-[10px] text-slate-400">/ 100</span>
    </div>
  );
}

export default function DocumentCompliance() {
  const [data, setData] = useState(null);      // { types, settings }
  const [ext, setExt] = useState([]);
  const [settings, setSettings] = useState(null);
  const [tmplImp, setTmplImp] = useState({});   // template_id -> importance
  const [saving, setSaving] = useState(false);
  const [newExt, setNewExt] = useState({ name: "", importance: 50 });

  const load = async () => {
    const [d, e] = await Promise.all([api.complianceTypes(), api.externalTypes()]);
    setData(d); setSettings(d.settings); setExt(e);
    setTmplImp(Object.fromEntries(d.types.filter((t) => t.kind === "tmpl").map((t) => [t.ref.slice(5), t.importance])));
  };
  useEffect(() => { load().catch(() => toast.error("Could not load compliance config.")); }, []);

  const templates = useMemo(() => (data?.types || []).filter((t) => t.kind === "tmpl"), [data]);

  const saveSettings = async () => {
    setSaving(true);
    try { await api.saveComplianceSettings(settings); toast.success("Mandatory mappings saved."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed."); } finally { setSaving(false); }
  };
  const saveTmplImp = async (tid) => {
    try { await api.setTemplateImportance({ template_id: tid, importance: tmplImp[tid] }); toast.success("Importance saved."); }
    catch { toast.error("Save failed."); }
  };
  const addExt = async () => {
    if (!newExt.name.trim()) return toast.error("Enter a type name.");
    try { await api.createExternalType(newExt); setNewExt({ name: "", importance: 50 }); toast.success("Type added."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Add failed."); }
  };
  const saveExt = async (t) => {
    try { await api.updateExternalType(t.id, { name: t.name, importance: t.importance, order: t.order || 0 }); toast.success("Saved."); }
    catch { toast.error("Save failed."); }
  };
  const delExt = async (t) => {
    if (!window.confirm(`Delete external type "${t.name}"?`)) return;
    try { await api.deleteExternalType(t.id); toast.success("Deleted."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed."); }
  };

  if (!settings) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="document-compliance-page">
      <div className="flex items-center gap-2.5">
        <ShieldCheck className="h-5 w-5" style={{ color: SLATE }} />
        <h1 className="text-xl font-semibold font-brand text-slate-800">Document Compliance</h1>
      </div>
      <p className="text-[12px] text-slate-500 -mt-3">Set how important each document type is (1–100) and which internal templates are the mandatory Support Plan and IRA. Importance drives each participant's compliance score.</p>

      {/* Mandatory mappings */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-4">
        <div className="text-sm font-semibold text-slate-800">Mandatory documents</div>
        <p className="text-[11px] text-slate-500 -mt-2">Every participant must have a <b>Service Agreement</b>, <b>Support Plan</b> and <b>IRA</b>. Map the two internal templates below; Service Agreement is built-in.</p>
        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <Label className="text-[11px] font-semibold text-slate-600">Support Plan template</Label>
            <Select value={settings.support_plan_template_id || "none"} onValueChange={(v) => setSettings((s) => ({ ...s, support_plan_template_id: v === "none" ? null : v }))}>
              <SelectTrigger className="h-9 text-sm mt-1" data-testid="map-support-plan"><SelectValue placeholder="Choose…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none" className="text-sm text-slate-400">Not set</SelectItem>
                {templates.map((t) => <SelectItem key={t.ref} value={t.ref.slice(5)} className="text-sm">{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] font-semibold text-slate-600">IRA template</Label>
            <Select value={settings.ira_template_id || "none"} onValueChange={(v) => setSettings((s) => ({ ...s, ira_template_id: v === "none" ? null : v }))}>
              <SelectTrigger className="h-9 text-sm mt-1" data-testid="map-ira"><SelectValue placeholder="Choose…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none" className="text-sm text-slate-400">Not set</SelectItem>
                {templates.map((t) => <SelectItem key={t.ref} value={t.ref.slice(5)} className="text-sm">{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] font-semibold text-slate-600">Service Agreement importance</Label>
            <div className="mt-2"><ImportanceInput value={settings.sa_importance} onChange={(v) => setSettings((s) => ({ ...s, sa_importance: v }))} testid="sa-importance" /></div>
          </div>
        </div>
        <div className="flex justify-end"><Button onClick={saveSettings} disabled={saving} style={{ backgroundColor: SLATE }} data-testid="save-mandatory">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" />Save</>}</Button></div>
      </div>

      {/* Internal template importance */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-3">
        <div className="text-sm font-semibold text-slate-800">Internal document importance</div>
        <div className="border border-slate-100 rounded-lg divide-y divide-slate-50 max-h-96 overflow-auto">
          {templates.length === 0 && <p className="px-3 py-4 text-xs text-slate-400">No document templates yet. Create them in Settings → Documents.</p>}
          {templates.map((t) => {
            const tid = t.ref.slice(5);
            return (
              <div key={t.ref} className="flex items-center gap-3 px-3 py-2">
                <span className="text-sm text-slate-700 flex-1 truncate">{t.name}{t.mandatory && <Badge className="ml-2 text-[9px] py-0 bg-slate-100 text-slate-500 border-slate-200">Mandatory</Badge>}</span>
                <ImportanceInput value={tmplImp[tid] ?? 50} onChange={(v) => setTmplImp((m) => ({ ...m, [tid]: v }))} testid={`tmpl-imp-${tid}`} />
                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => saveTmplImp(tid)} data-testid={`tmpl-imp-save-${tid}`}>Save</Button>
              </div>
            );
          })}
        </div>
      </div>

      {/* External document types */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-3">
        <div className="text-sm font-semibold text-slate-800">External document types</div>
        <p className="text-[11px] text-slate-500 -mt-1">Types for documents produced elsewhere (e.g. PBSP). These appear under Documents → External Documents on the participant.</p>
        <div className="flex items-end gap-2">
          <div className="flex-1"><Label className="text-[11px] font-semibold text-slate-600">New type name</Label>
            <Input value={newExt.name} onChange={(e) => setNewExt((n) => ({ ...n, name: e.target.value }))} placeholder="e.g. PBSP" className="h-9 text-sm mt-1" data-testid="new-ext-name" /></div>
          <div><Label className="text-[11px] font-semibold text-slate-600">Importance</Label>
            <div className="mt-1"><ImportanceInput value={newExt.importance} onChange={(v) => setNewExt((n) => ({ ...n, importance: v }))} testid="new-ext-importance" /></div></div>
          <Button onClick={addExt} style={{ backgroundColor: SLATE }} data-testid="add-ext-type"><Plus className="h-4 w-4 mr-1.5" />Add</Button>
        </div>
        <div className="border border-slate-100 rounded-lg divide-y divide-slate-50">
          {ext.length === 0 && <p className="px-3 py-4 text-xs text-slate-400">No external types yet.</p>}
          {ext.map((t, i) => (
            <div key={t.id} className="flex items-center gap-3 px-3 py-2">
              <Input value={t.name} onChange={(e) => setExt((rows) => rows.map((r, idx) => idx === i ? { ...r, name: e.target.value } : r))} className="h-8 text-sm flex-1" data-testid={`ext-name-${t.id}`} />
              <ImportanceInput value={t.importance} onChange={(v) => setExt((rows) => rows.map((r, idx) => idx === i ? { ...r, importance: v } : r))} testid={`ext-imp-${t.id}`} />
              <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => saveExt(t)} data-testid={`ext-save-${t.id}`}>Save</Button>
              <button onClick={() => delExt(t)} className="text-slate-300 hover:text-rose-600 p-1" data-testid={`ext-del-${t.id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
